import { z } from 'zod';
import { CurrencyCodeSchema, DateOnlySchema, IdSchema, ListQuerySchema, MoneyAmountSchema, PageInfoSchema, TimestampSchema, VersionSchema, } from './common.js';
/**
 * Customer receivables (specification 8.14): invoices drafted against a customer account,
 * issued and voided through named actions, payments recorded and allocated with exact money,
 * and the customer's balance and aging. Calculations are server-authoritative and versioned.
 */
export const InvoiceStatusSchema = z.enum([
    'draft',
    'issued',
    'partially_paid',
    'paid',
    'void',
    'uncollectible',
]);
export const PaymentMethodSchema = z.enum(['check', 'card', 'ach', 'cash', 'other']);
/** Quantities carry up to three decimals; every money value is an exact decimal string. */
export const QuantitySchema = z.string().regex(/^-?\d{1,9}(\.\d{1,3})?$/);
export const TaxRatePercentSchema = z.string().regex(/^\d{1,3}(\.\d{1,3})?$/);
export const InvoiceLineSchema = z.strictObject({
    id: IdSchema,
    position: z.number().int().nonnegative(),
    description: z.string(),
    quantity: QuantitySchema,
    unitPrice: MoneyAmountSchema,
    amount: MoneyAmountSchema,
    taxable: z.boolean(),
    workCategoryId: IdSchema.nullable(),
    estimateId: IdSchema.nullable(),
});
export const InvoiceLineInputSchema = z.strictObject({
    description: z.string().trim().min(1).max(500),
    quantity: QuantitySchema.default('1'),
    unitPrice: MoneyAmountSchema,
    taxable: z.boolean().default(true),
    workCategoryId: IdSchema.nullable().default(null),
    estimateId: IdSchema.nullable().default(null),
});
export const InvoiceSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    customerAccountId: IdSchema,
    jobFileId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    invoiceNumber: z.string(),
    status: InvoiceStatusSchema,
    currency: CurrencyCodeSchema,
    issueDate: DateOnlySchema.nullable(),
    dueDate: DateOnlySchema.nullable(),
    billingTerms: z.string().nullable(),
    taxRatePercent: TaxRatePercentSchema.nullable(),
    subtotal: MoneyAmountSchema,
    taxAmount: MoneyAmountSchema,
    total: MoneyAmountSchema,
    amountPaid: MoneyAmountSchema,
    balanceDue: MoneyAmountSchema,
    memo: z.string().nullable(),
    internalNotes: z.string().nullable(),
    issuedAt: TimestampSchema.nullable(),
    voidedAt: TimestampSchema.nullable(),
    voidReason: z.string().nullable(),
    uncollectibleAt: TimestampSchema.nullable(),
    uncollectibleReason: z.string().nullable(),
    calculationVersion: z.number().int().positive(),
    lines: z.array(InvoiceLineSchema),
    createdByMembershipId: IdSchema,
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
const EditableInvoiceFields = {
    jobFileId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    issueDate: DateOnlySchema.nullable(),
    dueDate: DateOnlySchema.nullable(),
    taxRatePercent: TaxRatePercentSchema.nullable(),
    memo: z.string().trim().max(2000).nullable(),
    internalNotes: z.string().trim().max(2000).nullable(),
    lines: z.array(InvoiceLineInputSchema).max(200),
};
export const CreateInvoiceRequestSchema = z.strictObject({
    customerAccountId: IdSchema,
    currency: CurrencyCodeSchema.optional(),
    jobFileId: EditableInvoiceFields.jobFileId.default(null),
    projectId: EditableInvoiceFields.projectId.default(null),
    issueDate: EditableInvoiceFields.issueDate.default(null),
    dueDate: EditableInvoiceFields.dueDate.default(null),
    taxRatePercent: EditableInvoiceFields.taxRatePercent.default(null),
    memo: EditableInvoiceFields.memo.default(null),
    internalNotes: EditableInvoiceFields.internalNotes.default(null),
    lines: EditableInvoiceFields.lines.default([]),
});
/** Drafts are edited in place; an issued invoice changes only through named actions. */
export const UpdateInvoiceRequestSchema = z.strictObject({
    version: VersionSchema,
    jobFileId: EditableInvoiceFields.jobFileId.optional(),
    projectId: EditableInvoiceFields.projectId.optional(),
    issueDate: EditableInvoiceFields.issueDate.optional(),
    dueDate: EditableInvoiceFields.dueDate.optional(),
    taxRatePercent: EditableInvoiceFields.taxRatePercent.optional(),
    memo: EditableInvoiceFields.memo.optional(),
    internalNotes: EditableInvoiceFields.internalNotes.optional(),
    lines: EditableInvoiceFields.lines.optional(),
});
export const IssueInvoiceRequestSchema = z.strictObject({
    version: VersionSchema,
    issueDate: DateOnlySchema.optional(),
    dueDate: DateOnlySchema.optional(),
});
export const InvoiceReasonRequestSchema = z.strictObject({
    version: VersionSchema,
    reason: z.string().trim().min(1).max(500),
});
export const InvoiceListQuerySchema = ListQuerySchema.extend({
    customerAccountId: IdSchema.optional(),
    jobFileId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    status: InvoiceStatusSchema.optional(),
});
export const InvoiceListSchema = z.strictObject({
    items: z.array(InvoiceSchema),
    page: PageInfoSchema,
});
export const PaymentAllocationSchema = z.strictObject({
    invoiceId: IdSchema,
    invoiceNumber: z.string(),
    amount: MoneyAmountSchema,
    createdAt: TimestampSchema,
});
export const PaymentSchema = z.strictObject({
    id: IdSchema,
    organizationId: IdSchema,
    customerAccountId: IdSchema,
    receivedOn: DateOnlySchema,
    amount: MoneyAmountSchema,
    currency: CurrencyCodeSchema,
    method: PaymentMethodSchema,
    reference: z.string().nullable(),
    memo: z.string().nullable(),
    allocatedAmount: MoneyAmountSchema,
    unallocatedAmount: MoneyAmountSchema,
    allocations: z.array(PaymentAllocationSchema),
    recordedByMembershipId: IdSchema,
    version: VersionSchema,
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
});
export const AllocationInputSchema = z.strictObject({
    invoiceId: IdSchema,
    amount: MoneyAmountSchema,
});
export const RecordPaymentRequestSchema = z.strictObject({
    customerAccountId: IdSchema,
    receivedOn: DateOnlySchema,
    amount: MoneyAmountSchema,
    currency: CurrencyCodeSchema.optional(),
    method: PaymentMethodSchema,
    reference: z.string().trim().max(100).nullable().default(null),
    memo: z.string().trim().max(1000).nullable().default(null),
    allocations: z.array(AllocationInputSchema).max(100).default([]),
});
export const AllocatePaymentRequestSchema = z.strictObject({
    version: VersionSchema,
    invoiceId: IdSchema,
    amount: MoneyAmountSchema,
});
export const UnallocatePaymentRequestSchema = z.strictObject({
    version: VersionSchema,
    invoiceId: IdSchema,
});
export const PaymentListQuerySchema = ListQuerySchema.extend({
    customerAccountId: IdSchema.optional(),
    invoiceId: IdSchema.optional(),
});
export const PaymentListSchema = z.strictObject({
    items: z.array(PaymentSchema),
    page: PageInfoSchema,
});
/** What the customer owes, by how overdue it is. Days are counted from the due date. */
export const ReceivablesAgingSchema = z.strictObject({
    current: MoneyAmountSchema,
    days1To30: MoneyAmountSchema,
    days31To60: MoneyAmountSchema,
    days61To90: MoneyAmountSchema,
    over90Days: MoneyAmountSchema,
});
export const CustomerReceivablesSchema = z.strictObject({
    customerAccountId: IdSchema,
    currency: CurrencyCodeSchema,
    asOf: DateOnlySchema,
    openInvoices: z.number().int().nonnegative(),
    balance: MoneyAmountSchema,
    unallocatedPayments: MoneyAmountSchema,
    aging: ReceivablesAgingSchema,
});
